import { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Layout, LayoutContent } from '@astryxdesign/core/Layout'
import { Stack } from '@astryxdesign/core/Stack'
import { Card } from '@astryxdesign/core/Card'
import { Avatar } from '@astryxdesign/core/Avatar'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import { Token } from '@astryxdesign/core/Token'
import { Button } from '@astryxdesign/core/Button'
import { Link } from '@astryxdesign/core/Link'
import { List, ListItem } from '@astryxdesign/core/List'
import { EmptyState } from '@astryxdesign/core/EmptyState'
import { Spinner } from '@astryxdesign/core/Spinner'
import StoreBadges from '../../components/StoreBadges'
import BookingButton from './BookingButton'
import { readCoach } from './coachApi'
import { APP_STORE_URL, PLAY_STORE_URL, coachPageLink } from './bookingLink'
import {
    coachName, kindLabel, levelLabel, placesLabel, placesLeft, priceLabel, scheduleLabel,
    seanceLabel, seriesTitle,
} from './coachText'

// A coach's page at padelr.app/coach/{username} - the link a coach shares
// (hidden-beacon-3). Who they are, where they teach, and what they offer:
// each open créneau with its séances still to come and the places left in
// each, then a button that hands the créneau over to the app, where booking
// happens.
//
// A coach with nothing open still has a page, with an empty state, so a link
// they shared last month never turns into an error.
//
// Same frame as /profile and /match, the other pages reached from a link by
// somebody who may not have an account.

// How many séances of one créneau are listed before the rest are summed up.
// A thirty-week récurrence is one offer, not thirty rows to scroll past.
const MAX_SEANCES = 4

function SeanceRow({ lesson, timezone }) {
    const label = placesLabel(lesson)
    const left = placesLeft(lesson)
    return (
        <ListItem
            label={seanceLabel(lesson, timezone)}
            endContent={label && (
                <Token size="sm" label={label} color={left === 0 ? 'gray' : 'green'} />
            )}
        />
    )
}

function SeriesCard({ series }) {
    const timezone = series.club && series.club.timezone
    const seances = series.lessons || []
    const shown = seances.slice(0, MAX_SEANCES)
    const more = seances.length - shown.length
    const level = levelLabel(series)
    const price = priceLabel(series)
    const title = seriesTitle(series)

    return (
        <Card width="100%">
            <Stack gap={4}>
                <Stack gap={1}>
                    <Heading level={3}>{title}</Heading>
                    <Text type="supporting">{scheduleLabel(series)}</Text>
                </Stack>

                <Stack direction="horizontal" gap={2} wrap="wrap">
                    {title !== kindLabel(series.kind) && <Token label={kindLabel(series.kind)} />}
                    {series.club && <Token label={series.club.name} color="blue" />}
                    {level && <Token label={level} />}
                    {price && <Token label={price} />}
                    {!series.has_room && <Token label="Complet" color="gray" />}
                </Stack>

                {shown.length > 0 && (
                    <List density="compact" hasDividers header={<Text type="label">Prochaines séances</Text>}>
                        {shown.map(lesson => (
                            <SeanceRow key={lesson.id} lesson={lesson} timezone={timezone} />
                        ))}
                    </List>
                )}
                {more > 0 && (
                    <Text type="supporting">
                        {more === 1 ? 'et 1 autre séance' : `et ${more} autres séances`}
                    </Text>
                )}

                <Stack direction="horizontal" hAlign="end">
                    <BookingButton seriesId={series.id} isFull={!series.has_room} />
                </Stack>
            </Stack>
        </Card>
    )
}

// navigator.share where there is one - every phone - and the clipboard
// otherwise.
function ShareButton({ username, name }) {
    const [copied, setCopied] = useState(false)

    const onClick = useCallback(async () => {
        const url = coachPageLink(username)
        try {
            if (navigator.share) {
                await navigator.share({ title: `${name} - coach sur Padelr`, url })
                return
            }
            await navigator.clipboard.writeText(url)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (e) {
            // Dismissing the share sheet rejects; nothing to say about it.
        }
    }, [username, name])

    return <Button variant="ghost" size="sm" label={copied ? 'Lien copié' : 'Partager'} onClick={onClick} />
}

function Frame({ children }) {
    return (
        <Layout padding={6} contentWidth={640} height="auto">
            <LayoutContent padding={6}>
                <Stack gap={8}>
                    {children}
                    <Stack gap={3} hAlign="center">
                        <Text type="supporting">Les inscriptions aux cours se font dans l'application</Text>
                        <StoreBadges appStoreURL={APP_STORE_URL} playStoreURL={PLAY_STORE_URL} />
                        <Link href="/coach">Tous les coachs</Link>
                    </Stack>
                </Stack>
            </LayoutContent>
        </Layout>
    )
}

export function CoachProfileView({ username }) {
    // undefined while loading, null when there is no such coach.
    const [data, setData] = useState(undefined)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        let live = true
        setData(undefined)
        setFailed(false)
        readCoach(username)
            .then(result => { if (live) setData(result) })
            .catch(() => { if (live) setFailed(true) })
        return () => { live = false }
    }, [username])

    useEffect(() => {
        if (data && data.coach) document.title = `${coachName(data.coach)} - Coach · Padelr`
    }, [data])

    if (failed) {
        return (
            <Frame>
                <EmptyState
                    headingLevel={1}
                    title="Page indisponible"
                    description="Nous n'avons pas pu charger ce coach. Réessayez dans un instant."
                />
            </Frame>
        )
    }

    if (data === undefined) {
        return (
            <Frame>
                <Stack gap={4} hAlign="center" paddingBlock={10}>
                    <Spinner />
                </Stack>
            </Frame>
        )
    }

    if (data === null || !data.coach) {
        return (
            <Frame>
                <EmptyState
                    headingLevel={1}
                    title="Coach introuvable"
                    description="Ce lien ne correspond à aucun coach Padelr."
                />
            </Frame>
        )
    }

    const { coach } = data
    const series = data.series || []
    const name = coachName(coach)

    return (
        <Frame>
            <Card width="100%">
                <Stack gap={4} hAlign="center">
                    <Avatar size="xl" name={name} />
                    <Stack gap={1} hAlign="center">
                        <Heading level={1} justify="center">{name}</Heading>
                        <Text type="supporting">Coach · @{coach.username}</Text>
                    </Stack>
                    {coach.clubs && coach.clubs.length > 0 && (
                        <Stack direction="horizontal" gap={2} wrap="wrap" hAlign="center">
                            {coach.clubs.map(club => (
                                <Token key={club.id} label={club.ville ? `${club.name} · ${club.ville}` : club.name} />
                            ))}
                        </Stack>
                    )}
                    <ShareButton username={coach.username} name={name} />
                </Stack>
            </Card>

            <Stack gap={4}>
                <Heading level={2}>Créneaux ouverts</Heading>
                {series.length === 0 ? (
                    <EmptyState
                        title="Aucun créneau ouvert"
                        description={`${name} n'a pas de cours ouvert aux inscriptions pour le moment.`}
                    />
                ) : (
                    series.map(s => <SeriesCard key={s.id} series={s} />)
                )}
            </Stack>
        </Frame>
    )
}

CoachProfileView.propTypes = {
    username: PropTypes.string.isRequired,
}

export default CoachProfileView
